/**
 * Drag a pinned operator workspace window by its header.
 * Immediate drag (title-bar style); position persisted as viewport %.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

const VIEWPORT_PAD = 8;

/**
 * @param {number} left
 * @param {number} top
 * @param {number} width
 * @param {number} height
 */
export function clampOperatorWorkspacePinnedRect(left, top, width, height) {
    const vw = typeof window !== 'undefined' ? window.innerWidth : 390;
    const vh = typeof window !== 'undefined' ? window.innerHeight : 700;
    const w = Math.max(160, Number(width) || 320);
    const h = Math.max(120, Number(height) || 360);
    const maxL = Math.max(VIEWPORT_PAD, vw - w - VIEWPORT_PAD);
    const maxT = Math.max(VIEWPORT_PAD, vh - h - VIEWPORT_PAD);
    return {
        left: Math.min(Math.max(VIEWPORT_PAD, Number(left) || VIEWPORT_PAD), maxL),
        top: Math.min(Math.max(VIEWPORT_PAD, Number(top) || VIEWPORT_PAD), maxT),
    };
}

/**
 * @param {{
 *   enabled?: boolean,
 *   shellRef: React.RefObject<HTMLElement|null>,
 *   readPinnedPosition: () => { leftPct: number, topPct: number }|null,
 *   writePinnedPosition: (pos: { leftPct: number, topPct: number }|null) => { leftPct: number, topPct: number }|null,
 * }} opts
 */
export function useOperatorWorkspacePinnedDrag({
    enabled = false,
    shellRef,
    readPinnedPosition,
    writePinnedPosition,
}) {
    const [pos, setPos] = useState(() => readPinnedPosition());
    const [moving, setMoving] = useState(false);
    const [sessionEpoch, setSessionEpoch] = useState(0);
    const sessionRef = useRef(null);

    const persistPos = useCallback(
        (next) => {
            const saved = writePinnedPosition(next);
            setPos(saved);
            return saved;
        },
        [writePinnedPosition]
    );

    const applyStoredToEl = useCallback((stored, el) => {
        if (!stored || !el) return null;
        const rect = el.getBoundingClientRect();
        const vw = window.innerWidth || 1;
        const vh = window.innerHeight || 1;
        const next = clampOperatorWorkspacePinnedRect(
            stored.leftPct * vw,
            stored.topPct * vh,
            rect.width,
            rect.height
        );
        el.style.setProperty('--op-msg-left', `${next.left}px`);
        el.style.setProperty('--op-msg-top', `${next.top}px`);
        return next;
    }, []);

    useEffect(() => {
        if (!enabled || !pos) return undefined;
        const onResize = () => {
            const el = shellRef.current;
            if (!el) return;
            applyStoredToEl(pos, el);
        };
        window.addEventListener('resize', onResize);
        onResize();
        return () => window.removeEventListener('resize', onResize);
    }, [enabled, pos, applyStoredToEl, shellRef]);

    useEffect(() => {
        if (!enabled || !pos) return;
        const el = shellRef.current;
        if (!el) return;
        applyStoredToEl(pos, el);
    }, [enabled, pos, applyStoredToEl, shellRef]);

    useEffect(() => {
        if (enabled) return;
        sessionRef.current = null;
        setMoving(false);
        document.body.classList.remove('op-msg-shell-moving');
        const el = shellRef.current;
        if (el) {
            el.style.removeProperty('--op-msg-left');
            el.style.removeProperty('--op-msg-top');
        }
    }, [enabled, shellRef]);

    const endSession = useCallback(
        (commit) => {
            const s = sessionRef.current;
            sessionRef.current = null;
            setMoving(false);
            document.body.classList.remove('op-msg-shell-moving');
            if (!s || !commit || !s.moved) return;
            const el = shellRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const clamped = clampOperatorWorkspacePinnedRect(
                rect.left,
                rect.top,
                rect.width,
                rect.height
            );
            const vw = window.innerWidth || 1;
            const vh = window.innerHeight || 1;
            persistPos({
                leftPct: clamped.left / vw,
                topPct: clamped.top / vh,
            });
            el.style.setProperty('--op-msg-left', `${clamped.left}px`);
            el.style.setProperty('--op-msg-top', `${clamped.top}px`);
        },
        [persistPos, shellRef]
    );

    useEffect(() => {
        if (!sessionEpoch) return undefined;

        const onMove = (e) => {
            const s = sessionRef.current;
            if (!s || !s.dragging) return;
            s.moved = true;
            const el = shellRef.current;
            if (!el) return;
            const nextLeft = s.startLeft + (e.clientX - s.originX);
            const nextTop = s.startTop + (e.clientY - s.originY);
            const clamped = clampOperatorWorkspacePinnedRect(
                nextLeft,
                nextTop,
                s.width,
                s.height
            );
            el.style.setProperty('--op-msg-left', `${clamped.left}px`);
            el.style.setProperty('--op-msg-top', `${clamped.top}px`);
        };

        const onUp = () => endSession(true);
        const onCancel = () => endSession(false);

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
        window.addEventListener('pointercancel', onCancel);
        return () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            window.removeEventListener('pointercancel', onCancel);
        };
    }, [sessionEpoch, endSession, shellRef]);

    const onHandlePointerDown = useCallback(
        (e) => {
            if (!enabled) return;
            if (e.button != null && e.button !== 0) return;
            if (e.target && e.target.closest && e.target.closest('button, a, input, select, textarea')) {
                return;
            }
            const el = shellRef.current;
            if (!el) return;

            e.preventDefault();
            try {
                e.currentTarget.setPointerCapture?.(e.pointerId);
            } catch {
                /* ignore */
            }

            const rect = el.getBoundingClientRect();
            sessionRef.current = {
                originX: e.clientX,
                originY: e.clientY,
                startLeft: rect.left,
                startTop: rect.top,
                width: rect.width,
                height: rect.height,
                dragging: true,
                moved: false,
            };
            setMoving(true);
            document.body.classList.add('op-msg-shell-moving');
            setSessionEpoch((n) => n + 1);

            if (!readPinnedPosition()) {
                const vw = window.innerWidth || 1;
                const vh = window.innerHeight || 1;
                const clamped = clampOperatorWorkspacePinnedRect(
                    rect.left,
                    rect.top,
                    rect.width,
                    rect.height
                );
                persistPos({
                    leftPct: clamped.left / vw,
                    topPct: clamped.top / vh,
                });
                el.style.setProperty('--op-msg-left', `${clamped.left}px`);
                el.style.setProperty('--op-msg-top', `${clamped.top}px`);
            }
        },
        [enabled, persistPos, readPinnedPosition, shellRef]
    );

    return {
        hasCustomPosition: Boolean(enabled && pos),
        moving,
        onHandlePointerDown,
    };
}
